import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface MentorshipTopicRequest {
  id: string;
  user_id: string;
  request_type: string;
  requested_skill_id: string | null;
  requested_industry_id: string | null;
  requested_mentor_topic_id: string | null;
  request_message: string;
  preferred_outcome: string | null;
  status: "pending_admin" | "in_progress" | "fulfilled" | "closed";
  assigned_admin_id: string | null;
  matched_mentor_id: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  user?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  skill?: {
    id: string;
    name: string;
  };
  industry?: {
    id: string;
    name: string;
  };
  mentor_topic?: {
    id: string;
    name: string;
  };
  matched_mentor?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  assigned_admin?: {
    id: string;
    full_name: string;
  };
}

// Fetch user's own topic requests
export function useMyMentorshipTopicRequests() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["my-mentorship-topic-requests", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("mentorship_topic_requests")
        .select(`
          id,
          user_id,
          request_type,
          requested_skill_id,
          requested_industry_id,
          requested_mentor_topic_id,
          request_message,
          preferred_outcome,
          status,
          matched_mentor_id,
          created_at,
          updated_at
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch related data
      const skillIds = data.filter(r => r.requested_skill_id).map(r => r.requested_skill_id!);
      const industryIds = data.filter(r => r.requested_industry_id).map(r => r.requested_industry_id!);
      const topicIds = data.filter(r => r.requested_mentor_topic_id).map(r => r.requested_mentor_topic_id!);
      const mentorIds = data.filter(r => r.matched_mentor_id).map(r => r.matched_mentor_id!);

      const [skills, industries, topics, mentors] = await Promise.all([
        skillIds.length > 0 
          ? supabase.from("skills").select("id, name").in("id", skillIds)
          : { data: [] },
        industryIds.length > 0 
          ? supabase.from("industries").select("id, name").in("id", industryIds)
          : { data: [] },
        topicIds.length > 0 
          ? supabase.from("mentor_topics").select("id, name").in("id", topicIds)
          : { data: [] },
        mentorIds.length > 0 
          ? supabase.from("profiles").select("id, full_name, avatar_url").in("id", mentorIds)
          : { data: [] },
      ]);

      return data.map(request => ({
        ...request,
        skill: skills.data?.find(s => s.id === request.requested_skill_id),
        industry: industries.data?.find(i => i.id === request.requested_industry_id),
        mentor_topic: topics.data?.find(t => t.id === request.requested_mentor_topic_id),
        matched_mentor: mentors.data?.find(m => m.id === request.matched_mentor_id),
      })) as MentorshipTopicRequest[];
    },
    enabled: !!user?.id,
  });
}

// Fetch all topic requests for admin
export function useAdminMentorshipTopicRequests(statusFilter?: string) {
  return useQuery({
    queryKey: ["admin-mentorship-topic-requests", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("mentorship_topic_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter && statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch related data
      const userIds = [...new Set(data.map(r => r.user_id))];
      const skillIds = data.filter(r => r.requested_skill_id).map(r => r.requested_skill_id!);
      const industryIds = data.filter(r => r.requested_industry_id).map(r => r.requested_industry_id!);
      const topicIds = data.filter(r => r.requested_mentor_topic_id).map(r => r.requested_mentor_topic_id!);
      const mentorIds = data.filter(r => r.matched_mentor_id).map(r => r.matched_mentor_id!);
      const adminIds = data.filter(r => r.assigned_admin_id).map(r => r.assigned_admin_id!);

      const [users, skills, industries, topics, mentors, admins] = await Promise.all([
        supabase.from("profiles").select("id, full_name, avatar_url").in("id", userIds),
        skillIds.length > 0 
          ? supabase.from("skills").select("id, name").in("id", skillIds)
          : { data: [] },
        industryIds.length > 0 
          ? supabase.from("industries").select("id, name").in("id", industryIds)
          : { data: [] },
        topicIds.length > 0 
          ? supabase.from("mentor_topics").select("id, name").in("id", topicIds)
          : { data: [] },
        mentorIds.length > 0 
          ? supabase.from("profiles").select("id, full_name, avatar_url").in("id", mentorIds)
          : { data: [] },
        adminIds.length > 0 
          ? supabase.from("profiles").select("id, full_name").in("id", adminIds)
          : { data: [] },
      ]);

      return data.map(request => ({
        ...request,
        user: users.data?.find(u => u.id === request.user_id),
        skill: skills.data?.find(s => s.id === request.requested_skill_id),
        industry: industries.data?.find(i => i.id === request.requested_industry_id),
        mentor_topic: topics.data?.find(t => t.id === request.requested_mentor_topic_id),
        matched_mentor: mentors.data?.find(m => m.id === request.matched_mentor_id),
        assigned_admin: admins.data?.find(a => a.id === request.assigned_admin_id),
      })) as MentorshipTopicRequest[];
    },
  });
}

// Count pending requests for admin badge
export function usePendingTopicRequestCount() {
  return useQuery({
    queryKey: ["pending-topic-request-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("mentorship_topic_requests")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending_admin");

      if (error) throw error;
      return count || 0;
    },
  });
}

// Create a new topic request
export function useCreateMentorshipTopicRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (request: {
      requested_skill_id?: string;
      requested_industry_id?: string;
      requested_mentor_topic_id?: string;
      request_message: string;
      preferred_outcome?: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("mentorship_topic_requests")
        .insert({
          user_id: user.id,
          ...request,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-mentorship-topic-requests"] });
      queryClient.invalidateQueries({ queryKey: ["admin-mentorship-topic-requests"] });
      queryClient.invalidateQueries({ queryKey: ["pending-topic-request-count"] });
      toast.success("Your mentorship request has been submitted for review");
    },
    onError: (error) => {
      toast.error("Failed to submit request: " + error.message);
    },
  });
}

// Admin: Update a topic request
export function useUpdateMentorshipTopicRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: Partial<{
        status: string;
        assigned_admin_id: string | null;
        matched_mentor_id: string | null;
        admin_notes: string;
      }>;
    }) => {
      const { data, error } = await supabase
        .from("mentorship_topic_requests")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-mentorship-topic-requests"] });
      queryClient.invalidateQueries({ queryKey: ["my-mentorship-topic-requests"] });
      queryClient.invalidateQueries({ queryKey: ["pending-topic-request-count"] });
      toast.success("Request updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update request: " + error.message);
    },
  });
}
